import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, ExternalLink, Info } from "lucide-react";

export default function AdminTryOnSettings() {
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tryon/status").then(r => { setStatus(r.data); setGlobalEnabled(!!r.data.global_enabled); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/try-on", { global_enabled: globalEnabled });
      const s = await api.get("/tryon/status");
      setStatus(s.data);
      toast.success("Try-On settings saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold inline-flex items-center gap-2">
          <Sparkles className="text-[#FF4500]"/> Virtual Try-On
        </h1>
        <p className="text-slate-500 text-sm mt-1">Global controls for the AI/AR shopping experience. Per-product settings live on each product edit page.</p>
      </div>

      {/* Provider status */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5" data-testid="provider-status-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">AI Provider</div>
            <div className="font-display text-xl font-bold mt-1">{status?.provider || "Not configured"}</div>
          </div>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${status?.provider_configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {status?.provider_configured ? "CONNECTED" : "SHELL MODE"}
          </span>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
          <Feature label="Photo Try-On" ok={status?.supports_photo}/>
          <Feature label="Live Camera Try-On" ok={status?.supports_live}/>
        </div>
        <div className="mt-4 bg-slate-50 rounded-xl p-3 text-xs text-slate-600 flex gap-2 items-start">
          <Info size={14} className="text-slate-400 mt-0.5 shrink-0"/>
          <div>
            <b>Shell mode</b> means the UI, admin controls, database fields, and cart integration are fully live — <b>no</b> paid AI credits are being used yet. To activate:
            set <code className="bg-white px-1 rounded">TRYON_PROVIDER=fashn</code> (or <code className="bg-white px-1 rounded">google</code>, <code className="bg-white px-1 rounded">deepar</code>) and <code className="bg-white px-1 rounded">TRYON_PROVIDER_KEY=…</code> in <code className="bg-white px-1 rounded">backend/.env</code>, then restart the backend.
          </div>
        </div>
      </div>

      {/* Global toggle */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5">
        <div className="flex items-start gap-4">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#004BCA]/10 text-[#004BCA]"><ShieldCheck size={20}/></span>
          <div className="flex-1">
            <div className="font-display font-bold">Enable Virtual Try-On globally</div>
            <p className="text-sm text-slate-500 mt-1">When OFF, the TRY IT ON button is hidden on all product pages regardless of individual product settings.</p>
          </div>
          <label className="relative inline-block w-12 h-7 cursor-pointer shrink-0">
            <input data-testid="global-toggle" type="checkbox" checked={globalEnabled} onChange={e=>setGlobalEnabled(e.target.checked)} className="sr-only peer"/>
            <span className="absolute inset-0 rounded-full bg-slate-200 peer-checked:bg-[#004BCA] transition"/>
            <span className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"/>
          </label>
        </div>
        <button data-testid="save-tryon-settings" onClick={save} disabled={saving} className="btn-primary-sf mt-5 px-6 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {/* Provider comparison teaser */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5">
        <h3 className="font-display font-bold mb-3">Available providers</h3>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <Provider name="FASHN AI" price="$0.075/img" best="Photo apparel — best fabric fidelity" href="https://fashn.ai/products/api"/>
          <Provider name="Google Vertex" price="$0.06/img" best="Photo apparel — 4 outputs per call" href="https://cloud.google.com/vertex-ai"/>
          <Provider name="DeepAR" price="$25–1000/mo" best="Live camera — jewelry, bags, shoes" href="https://developer.deepar.ai/pricing"/>
        </div>
      </div>
    </div>
  );
}

function Feature({ label, ok }) {
  return (
    <div className={`flex items-center gap-2 p-3 rounded-xl border ${ok ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
      <span className={`w-2 h-2 rounded-full ${ok?"bg-emerald-500":"bg-slate-300"}`}/>
      <span className="font-medium">{label}</span>
      <span className={`ml-auto text-xs ${ok?"text-emerald-700":"text-slate-400"}`}>{ok?"Active":"Off"}</span>
    </div>
  );
}
function Provider({ name, price, best, href }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="block p-3 rounded-xl border border-slate-200 hover:border-[#004BCA]">
      <div className="font-semibold text-sm inline-flex items-center gap-1">{name} <ExternalLink size={10} className="text-slate-400"/></div>
      <div className="text-xs text-[#004BCA] font-mono mt-0.5">{price}</div>
      <div className="text-xs text-slate-500 mt-1">{best}</div>
    </a>
  );
}
